// src/components/admin/EditPromptModal.jsx
import React, { useEffect, useState } from "react";
import {
  Modal,
  Badge,
  Button,
  Row,
  Col,
  Form,
  Alert,
} from "react-bootstrap";

/**
 * EditPromptModal
 *
 * Props:
 * - show: boolean
 * - onHide: () => void
 * - prompt: the currently selected prompt version object (or null)
 * - onSave: (formValues) => Promise<void> | void
 * - sources: string[]  (for datalist of known sources)
 */
export default function EditPromptModal({
  show,
  onHide,
  prompt,
  onSave,
  sources = [],
}) {
  const [form, setForm] = useState({
    name: "",
    source: "",
    prompt_text: "",
    description: "",
    persona: "",
    model: "",
    udf1: "",
    udf2: "",
    udf3: "",
    created_by: "",
    updated_by: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Populate form whenever modal opens or prompt changes
  useEffect(() => {
    if (show && prompt) {
      setForm({
        name: prompt.name || "",
        source: prompt.source || "",
        prompt_text: prompt.prompt_text || "",
        description: prompt.description || "",
        persona: prompt.persona || "",
        model: prompt.model || "",
        udf1: prompt.udf1 || "",
        udf2: prompt.udf2 || "",
        udf3: prompt.udf3 || "",
        created_by: prompt.created_by || "",
        updated_by: prompt.updated_by || "",
      });
      setError(null);
      setBusy(false);
    }
  }, [show, prompt]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!prompt || !onSave) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(form);
      setBusy(false);
      onHide?.();
    } catch (e) {
      setBusy(false);
      setError(e?.message || "Failed to update prompt.");
    }
  };

  const field = (label, key, props = {}) => (
    <Form.Group className="ep-group">
      <Form.Label className="pv-label">{label}</Form.Label>
      <Form.Control
        type="text"
        value={form[key]}
        onChange={handleChange(key)}
        {...props}
      />
    </Form.Group>
  );

  return (
    <Modal
      show={show}
      onHide={busy ? undefined : onHide}
      size="lg"
      centered
      scrollable
      dialogClassName="ep-dialog"
    >
      <Modal.Header closeButton={!busy} className="pv-header">
        <div className="w-100">
          <div className="pv-eyebrow">Edit Prompt</div>
          <div className="pv-title text-break">
            {prompt?.name || "Prompt"}
          </div>
          {prompt && (
            <div className="pv-active-line">
              <span>Editing version</span>
              <Badge bg={prompt.active ? "success" : "secondary"} pill>
                v{Number(prompt.version)}
                {prompt.active ? " · Active" : ""}
              </Badge>
            </div>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert
            variant="danger"
            className="mb-3 py-2"
            onClose={() => setError(null)}
            dismissible
          >
            {error}
          </Alert>
        )}

        {!prompt ? (
          <div className="text-muted small">No prompt selected.</div>
        ) : (
          <>
            <div className="pv-section-title">Prompt Text</div>
            <Form.Control
              as="textarea"
              className="ep-prompt"
              value={form.prompt_text}
              onChange={handleChange("prompt_text")}
              placeholder="System/assistant prompt text"
              spellCheck={false}
            />

            <div className="pv-section-title">Configuration</div>
            <Row className="g-3">
              <Col md={6}>{field("Name", "name", { placeholder: "Prompt name" })}</Col>
              <Col md={6}>
                <Form.Group className="ep-group">
                  <Form.Label className="pv-label">Source</Form.Label>
                  <Form.Control
                    list="edit-known-sources"
                    value={form.source}
                    onChange={handleChange("source")}
                    placeholder="e.g., fxgpt, emgpt"
                  />
                  <datalist id="edit-known-sources">
                    {sources.map((src) => (
                      <option key={src} value={src} />
                    ))}
                  </datalist>
                </Form.Group>
              </Col>
              <Col md={6}>{field("Model", "model")}</Col>
              <Col md={6}>{field("Persona", "persona")}</Col>
              <Col md={12}>
                {field("Description", "description", {
                  placeholder: "Short summary",
                })}
              </Col>
            </Row>

            <div className="pv-section-title">User-Defined Fields</div>
            <Row className="g-3">
              <Col md={4}>{field("UDF1", "udf1")}</Col>
              <Col md={4}>{field("UDF2", "udf2")}</Col>
              <Col md={4}>{field("UDF3", "udf3")}</Col>
            </Row>

            <div className="pv-section-title">Audit</div>
            <Row className="g-3">
              <Col md={6}>
                {field("Created By (read-only)", "created_by", {
                  disabled: true,
                })}
              </Col>
              <Col md={6}>{field("Updated By", "updated_by")}</Col>
            </Row>

            <div className="ep-note">
              The active version is controlled by the <strong>Activate</strong>{" "}
              button in the prompt viewer.
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="ep-footer">
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={busy || !prompt}
        >
          {busy ? "Saving…" : "Save Changes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
